import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Capacitor } from '@capacitor/core';
import { Eye, EyeOff } from 'lucide-react';
import AppleSignInButton from '../components/AppleSignInButton';
import { getApiBase } from '../utils/apiBase';
import { resolveGoogleOAuthRedirectBase } from '../utils/oauthRedirectBase';
import { buildWebAuthCallbackUrl, startGoogleOAuthNavigation } from '../utils/oauthNativeHandoff';
import { t } from '../locales';

const inputFocusClass =
  'bg-black/50 border-white/20 text-white h-11 transition-[border-color,box-shadow] duration-200 ' +
  'focus-visible:outline-none focus-visible:border-violet-500/55 focus-visible:ring-2 focus-visible:ring-violet-500/25 focus-visible:shadow-[0_0_0_1px_rgba(139,92,246,0.15)]';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const id = location.state?.authToastId;
    if (!id) return;
    if (id === 'session_expired') {
      toast.error(t('auth.sessionExpired'), { id: 'auth-dekosta-session-expired' });
    } else if (id === 'user_not_found') {
      toast.error(t('auth.userNotFound'), { id: 'auth-dekosta-user-not-found' });
    }
    const next = { ...(location.state || {}) };
    delete next.authToastId;
    navigate(location.pathname + (location.search || ''), {
      replace: true,
      state: Object.keys(next).length ? next : null,
    });
  }, [location.state, location.pathname, location.search, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedEmail = typeof email === 'string' ? email.trim() : '';
    if (!trimmedEmail || !password) {
      toast.error(t('auth.login.toastEmailPasswordRequired'));
      return;
    }

    setLoading(true);

    try {
      await login(trimmedEmail, password);
      toast.success(t('auth.login.toastWelcomeBack'));
      const from = location.state?.from;
      const dest =
        from && typeof from.pathname === 'string'
          ? `${from.pathname}${from.search || ''}`
          : '/dashboard';
      navigate(dest, { replace: true });
    } catch (error) {
      console.error('[auth/login] failed', {
        stage: error?.stage || 'auth_login',
        status: error?.response?.status || null,
        data: error?.response?.data,
        loginResponse: error?.loginResponse || null,
        authCheck: error?.authCheck || null,
        message: error?.message,
      });
      const msg =
        error.response?.data?.detail || 
        (typeof error?.message === 'string' ? error.message : String(error?.message || '')) || 
        t('auth.login.loginFailedFallback');
      toast.error(Array.isArray(msg) ? msg.join(', ') : String(msg));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    const resolved = resolveGoogleOAuthRedirectBase();
    if (!resolved.ok) {
      toast.error(resolved.message);
      return;
    }
    const redirectAfter = buildWebAuthCallbackUrl(resolved.base, Capacitor.isNativePlatform());
    const apiBase = getApiBase();
    const authUrl = `${apiBase}/api/auth/google?redirect_after=${encodeURIComponent(redirectAfter)}`;
    await startGoogleOAuthNavigation(authUrl);
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-8"
      style={{
        background: 'radial-gradient(circle at center, #2d1b4e 0%, #050505 70%)',
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.28 }}
        className="w-full max-w-[min(100%,22rem)]"
      >
        <div className="glass-effect rounded-2xl p-6 sm:p-7 border border-white/5 shadow-xl shadow-black/40">
          <header className="text-center mb-6">
            <p
              className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2"
              style={{ fontFamily: 'Cinzel, serif' }}
            >
              {t('common.brand')}
            </p>
            <h1
              className="text-2xl sm:text-[1.65rem] font-bold text-white leading-tight"
              style={{ fontFamily: 'Cinzel, serif' }}
            >
              {t('auth.login.title')}
            </h1>
            <p className="text-sm text-zinc-400 mt-2 leading-snug">{t('auth.login.subtitle')}</p>
          </header>

          <form data-testid="login-form" onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-medium text-zinc-400">
                {t('common.email')}
              </Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`mt-0 ${inputFocusClass}`}
                placeholder={t('auth.login.placeholderEmail')}
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-medium text-zinc-400">
                {t('common.password')}
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`mt-0 pr-11 ${inputFocusClass}`}
                  placeholder={t('auth.login.passwordDots')}
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  className="absolute right-1 top-1/2 -translate-y-1/2 p-2 rounded-lg text-zinc-500 hover:text-zinc-200 hover:bg-white/5 transition-colors"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? t('auth.login.hidePassword') : t('auth.login.showPassword')}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="pt-1 space-y-3">
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 btn-primary font-semibold"
                data-testid="login-submit-button"
              >
                {loading ? t('auth.login.submitLoading') : t('auth.login.submit')}
              </Button>
              <p className="text-center text-[11px] text-zinc-500/90 leading-snug">
                {t('auth.login.secureNote')}
              </p>
            </div>
          </form>

          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10" />
            </div>
            <div className="relative flex justify-center text-[11px]">
              <span className="px-2 bg-[#0c0c0f]/90 text-zinc-500">{t('common.or')}</span>
            </div>
          </div>

          <Button
            type="button"
            onClick={handleGoogleLogin}
            variant="outline"
            className="w-full h-11 border-white/20 hover:bg-white/5 text-zinc-100"
            data-testid="google-login-button"
          >
            <svg className="w-5 h-5 mr-2 shrink-0" viewBox="0 0 24 24" aria-hidden>
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {t('auth.login.googleContinue')}
          </Button>

          <div className="mt-3">
            <AppleSignInButton
              legal={{ ageConfirmed: true, kvkkAccepted: true, termsAccepted: true }}
              redirectTo="/dashboard"
              label="Apple ile devam et"
            />
            <p className="text-[10px] text-zinc-500 text-center mt-2 leading-snug">
              Yeni hesap için Apple ile devam ederseniz{' '}
              <a href="/kvkk" target="_blank" rel="noreferrer" className="text-violet-400 hover:text-violet-300 underline">
                KVKK
              </a>{' '}
              ve{' '}
              <a href="/terms" target="_blank" rel="noreferrer" className="text-violet-400 hover:text-violet-300 underline">
                Kullanım Koşulları
              </a>
              &apos;nı kabul etmiş sayılırsınız.
            </p>
          </div>

          <div className="mt-6 space-y-3 text-center text-sm">
            <p>
              <Link
                to="/forgot-password"
                className="text-violet-400 hover:text-violet-300 font-medium"
              >
                {t('auth.login.forgotPassword')}
              </Link>
            </p>
            <p className="text-zinc-400">
              {t('auth.login.noAccount')}{' '}
              <Link to="/register" className="text-violet-400 hover:text-violet-300 font-medium">
                {t('auth.login.registerLink')}
              </Link>
            </p>
          </div>

          <p className="text-center mt-4 text-xs text-zinc-500">
            <Link to="/privacy" className="text-zinc-400 hover:text-violet-300 underline">
              {t('common.privacyPolicy')}
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;
